<template>
  <Teleport to="body">
    <Transition name="slide-up" appear>
      <div v-if="detail" class="fixed inset-0 z-[60] bg-black/40" @click.self="close">
        <div
          class="surface fixed inset-x-0 bottom-0 flex max-h-[85dvh] flex-col overflow-y-auto rounded-t-lg px-3 pt-4 pb-[max(1.25rem,env(safe-area-inset-bottom))] shadow-xl sm:inset-x-auto sm:top-1/2 sm:bottom-auto sm:left-1/2 sm:-translate-x-1/2 sm:-translate-y-1/2 sm:rounded-md sm:pb-4"
          :class="
            detail.kind === 'location'
              ? 'sm:max-h-[min(42rem,calc(100dvh-2rem))] sm:w-[min(36rem,calc(100vw-1rem))]'
              : 'sm:max-h-[min(28rem,calc(100dvh-2rem))] sm:w-[min(22rem,calc(100vw-1rem))]'
          "
        >
          <div class="mb-1 flex items-center justify-between gap-x-2">
            <h2 class="font-heading min-w-0 truncate text-base">{{ title }}</h2>
            <GeneralButton
              title="Sluiten"
              color="surface"
              size="small"
              variant="transparent"
              :icon="['fas', 'xmark']"
              class="shrink-0"
              @click="close"
            />
          </div>

          <div v-if="entity">
            <div v-if="detail.kind === 'location'" class="mb-2">
              <LocationReadOnlyMap :location="entity as Location" />
            </div>

            <div class="space-y-2 text-sm">
              <div v-for="field in fields" :key="field.label">
                <dt class="font-heading text-sm">{{ field.label }}</dt>
                <dd class="mt-0.5" :class="field.multiline ? 'whitespace-pre-wrap' : ''">
                  <a
                    v-if="field.mailto"
                    :href="`mailto:${field.value}`"
                    class="text-primary hover:underline"
                  >
                    {{ field.value }}
                  </a>
                  <MentionText
                    v-else-if="field.mention"
                    class="inline wrap-break-word whitespace-pre-wrap"
                    :text="field.value"
                    prefer-location-address
                  />
                  <template v-else>{{ field.value }}</template>
                </dd>
              </div>
            </div>

            <div v-if="action" class="mt-2 space-y-2">
              <a v-if="action.href" :href="action.href" class="block">
                <GeneralButton
                  variant="outlined"
                  :title="action.title"
                  :icon="action.icon"
                  class="w-full"
                >
                  {{ action.title }}
                </GeneralButton>
              </a>
              <GeneralButton
                v-else
                variant="outlined"
                :title="action.title"
                :icon="action.icon"
                class="w-full"
                @click="action.onClick?.()"
              >
                {{ action.title }}
              </GeneralButton>
            </div>
          </div>
          <p v-else class="text-on-surface/75 dark:text-on-surface-dark/75 text-sm italic">
            Gegevens niet beschikbaar.
          </p>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { computed } from "vue"
import { onKeyStroke } from "@vueuse/core"
import GeneralButton from "@/components/inputs/GeneralButton.vue"
import MentionText from "@/components/message/MentionText.vue"
import LocationReadOnlyMap from "@/components/mention/LocationReadOnlyMap.vue"
import {
  closeMentionDetail as close,
  mentionDetail as detail
} from "@/composables/mention/use-mention-detail"
import { useMention } from "@/composables/mention/use-mention"
import {
  formatKnownBusinessEntityAddress,
  formatPostalAddress,
  getKnownBusinessEntityMainLocation
} from "@/helper/address/format-postal-address"
import { buildMapsNavigationUrl, openMapsNavigation } from "@/helper/location/location-helpers"
import type { KnownBusinessEntityContact } from "@/models/known-business-entity-contact.model"
import type { Project } from "@/models/project.model"
import type { KnownBusinessEntity } from "@/models/known-business-entity.model"
import type { Location } from "@/models/location.model"

type Field = {
  label: string
  value: string
  multiline?: boolean
  mailto?: boolean
  mention?: boolean
}
type Action = { title: string; icon: string[]; href?: string; onClick?: () => void }

const TITLES = {
  person: "Contactpersoon",
  project: "Project",
  business: "Bedrijf",
  location: "Locatie"
} as const

const { resolveEntity } = useMention()

const entity = computed(() =>
  detail.value ? resolveEntity(detail.value.kind, detail.value.id) : null
)

const title = computed(() => (detail.value ? TITLES[detail.value.kind] : ""))

const fields = computed<Field[]>(() => {
  const kind = detail.value?.kind
  const model = entity.value
  if (!kind || !model) return []
  const out: Field[] = []
  const push = (label: string, value: string | null | undefined, extra?: Partial<Field>) => {
    if (value) out.push({ label, value, ...extra })
  }

  if (kind === "person") {
    const contact = model as KnownBusinessEntityContact
    push("Naam", contact.person?.fullName?.trim())
    push("Telefoon", contact.phoneNumber)
    push("E-mail", contact.email, { mailto: true })
    push("Adres", formatPostalAddress(contact.person?.address), { multiline: true })
  } else if (kind === "business") {
    const business = model as KnownBusinessEntity
    push("Bedrijfsnaam", business.name)
    push("Adres", formatKnownBusinessEntityAddress(business), { multiline: true })
  } else if (kind === "project") {
    const project = model as Project
    push("Projectnaam", project.name)
    push("Contactpersoon", project.contact?.person?.fullName?.trim())
    push("Projectopmerking voor dienstverleners", project.descriptionServiceProvider, {
      multiline: true,
      mention: true
    })
  } else {
    const location = model as Location
    push("Locatienaam", location.name?.trim())
    push("Adres", location.address?.trim(), { multiline: true })
  }
  return out
})

const action = computed<Action | null>(() => {
  const kind = detail.value?.kind
  const model = entity.value
  if (!kind || !model) return null

  if (kind === "person") {
    const phone = (model as KnownBusinessEntityContact).getNormalizedPhoneNumber()
    return phone ? { title: "Bellen", icon: ["fas", "phone"], href: `tel:${phone}` } : null
  }
  if (kind === "business") {
    const business = model as KnownBusinessEntity
    const mainLocation = getKnownBusinessEntityMainLocation(business)
    const url = buildMapsNavigationUrl(
      mainLocation?.latitude,
      mainLocation?.longitude,
      formatKnownBusinessEntityAddress(business)
    )
    return url
      ? {
          title: "Navigeren",
          icon: ["fas", "location-arrow"],
          onClick: () => openMapsNavigation(url)
        }
      : null
  }
  if (kind === "location") {
    const location = model as Location
    const url = buildMapsNavigationUrl(
      location.latitude,
      location.longitude,
      location.address?.trim()
    )
    return url
      ? {
          title: "Navigeren",
          icon: ["fas", "diamond-turn-right"],
          onClick: () => openMapsNavigation(url)
        }
      : null
  }
  return null
})

onKeyStroke("Escape", () => close())
</script>

<style scoped>
/* Overlay fades while the sheet slides in from below; on desktop the panel
   slides up into its centered position (the Tailwind `translate` centering is
   a separate CSS property, so it composes with this animated transform). */
.slide-up-enter-active,
.slide-up-leave-active {
  transition: opacity 0.3s ease-out;
}

.slide-up-enter-active > div,
.slide-up-leave-active > div {
  transition: transform 0.3s ease-out;
}

.slide-up-enter-from,
.slide-up-leave-to {
  opacity: 0;
}

.slide-up-enter-from > div,
.slide-up-leave-to > div {
  transform: translateY(100%);
}
</style>
